import React, { useMemo, useRef } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Tooltip from '../../atoms/Tooltip/Tooltip';
import { agenticMarkdownComponents, fixUnclosedColorSpans } from '../../shared/markdownComponents';
// import DownloadFile from '../../atoms/Icon/DownloadFile';
// import * as XLSX from 'xlsx';
import './DataTable.css';

/** Must match `.data-table .ant-table-tbody > tr > td { height }` in DataTable.css (used for scroll.y sizing). */
const DATA_TABLE_BODY_ROW_HEIGHT_PX = 38;

export interface DataTableColumn {
    title: string;
    dataIndex: string;
    key: string;
    align?: 'left' | 'center' | 'right';
    width?: number;
    fixed?: 'left' | 'right';
    render?: (text: any, record: any, index: number) => React.ReactNode;
}

export interface VizDataFormat {
    chart_type?: string;
    chartTitle?: string;
    data: any[][];
}

export interface DataTableProps {
    /** Title to display above the table */
    title?: string;
    /** Column definitions for the table */
    columns?: DataTableColumn[];
    /** Data source for table rows */
    dataSource?: any[];
    /** Viz data format - when provided, columns and dataSource will be auto-generated */
    vizData?: VizDataFormat;
    /** Unique key for each row */
    rowKey?: string;
    /** Enable/disable table borders */
    bordered?: boolean;
    /** Pagination configuration */
    pagination?: false | object;
    /** Enable horizontal scroll */
    scroll?: { x?: string | number | true; y?: string | number };
    /**
     * When set, caps the table body scroll height to this many data rows (not counting the header).
     * Ant Design keeps the column header row fixed while the body scrolls.
     */
    maxScrollBodyRows?: number;
    /** Enable download functionality */
    onDownload?: () => void;
    /** CSS class name for custom styling */
    className?: string;
    /** Maximum width for the table container */
    maxWidth?: string | number;
    /** Loading state */
    loading?: boolean;
    /** Data test ID for testing */
    'data-test-id'?: string;
}

/** Check if string contains HTML (e.g. color spans) that needs markdown rendering */
const containsHtml = (s: string) => /<\s*span[\s>]/i.test(s) || /<[a-z][\s\S]*>/i.test(s);

/** Render table cell content - supports markdown/HTML for red/green pills */
const renderCellContent = (value: any): React.ReactNode => {
    if (value === null || value === undefined) {
        return null;
    }
    const str = String(value);
    if (!str) return null;
    if (containsHtml(str)) {
        return (
            <div className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] markdown-content">
                <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm]}
                    remarkRehypeOptions={{ allowDangerousHtml: true }}
                    components={agenticMarkdownComponents}
                >
                    {fixUnclosedColorSpans(str)}
                </ReactMarkdown>
            </div>
        );
    }
    return str;
};

/**
 * Transform viz_data format to columns and dataSource
 */
const transformVizData = (vizData: VizDataFormat): { columns: DataTableColumn[]; dataSource: any[] } => {
    if (!vizData?.data || !Array.isArray(vizData.data) || vizData.data.length === 0) {
        return { columns: [], dataSource: [] };
    }

    // First row contains column headers
    const headers = vizData.data[0];
    if (!headers || !Array.isArray(headers)) {
        return { columns: [], dataSource: [] };
    }

    // Create columns from headers
    const columns: DataTableColumn[] = headers.map((header, index) => {
        const headerStr = String(header);
        return {
            title: headerStr,
            dataIndex: `col_${index}`,
            key: `col_${index}`,
            align: 'left' as const,
        };
    });

    // Transform data rows to objects
    const dataSource = vizData.data.slice(1).map((row, rowIndex) => {
        const rowObj: any = { key: `row_${rowIndex}` };
        headers.forEach((_, colIndex) => {
            // Handle rows with fewer columns than headers
            const value = colIndex < row.length ? row[colIndex] : null;
            rowObj[`col_${colIndex}`] = value ?? null;
        });
        return rowObj;
    });

    return { columns, dataSource };
};

const DataTable: React.FC<DataTableProps> = ({
    title,
    columns: providedColumns,
    dataSource: providedDataSource,
    vizData,
    rowKey = 'key',
    bordered = true,
    pagination = false,
    scroll = { x: 'max-content' },
    maxScrollBodyRows,
    onDownload,
    className = '',
    maxWidth = '1112px',
    loading = false,
    'data-test-id': dataTestId,
}) => {
    const tableRef = useRef<HTMLDivElement>(null);

    // Transform vizData if provided, otherwise use provided columns and dataSource
    const { columns: transformedColumns, dataSource: transformedDataSource } = useMemo(() => {
        if (vizData) {
            const result = transformVizData(vizData);
            return {
                columns: result.columns,
                dataSource: result.dataSource,
            };
        }
        return {
            columns: providedColumns || [],
            dataSource: providedDataSource || [],
        };
    }, [vizData, providedColumns, providedDataSource]);

    // Use title from vizData if not provided
    const displayTitle = title || vizData?.chartTitle;

    const mergedScroll = useMemo(() => {
        const next = { ...scroll };
        if (maxScrollBodyRows != null && maxScrollBodyRows > 0) {
            next.y = maxScrollBodyRows * DATA_TABLE_BODY_ROW_HEIGHT_PX;
        }
        return next;
    }, [scroll, maxScrollBodyRows]);

    const finalColumns: ColumnsType<any> = transformedColumns.map((col) => ({
        ...col,
        title:
            typeof col.title === 'string' && col.title.length > 30 ? (
                <Tooltip title={col.title}>
                    <span className="truncate">{col.title}</span>
                </Tooltip>
            ) : (
                <span style={{ whiteSpace: 'nowrap' }}>{col.title}</span>
            ),
        align: col.align || 'left',
        render: col.render ?? ((value: any) => renderCellContent(value)),
    }));

    // Default Excel export function
    // const handleDefaultExcelExport = async () => {
    //     if (!tableRef.current) {
    //         console.error('Table container ref is not available');
    //         return;
    //     }

    //     try {
    //         // Wait a bit for table to finish rendering
    //         await new Promise((resolve) => setTimeout(resolve, 200));

    //         // Get headers from columns
    //         const headers: string[] = transformedColumns.map((col) => {
    //             if (typeof col.title === 'string') {
    //                 return col.title;
    //             }
    //             // Handle React nodes - try to extract text
    //             if (col.title && typeof col.title === 'object' && 'props' in col.title) {
    //                 const titleNode = col.title as any;
    //                 if (titleNode.props?.title) {
    //                     return titleNode.props.title;
    //                 }
    //                 if (titleNode.props?.children) {
    //                     return String(titleNode.props.children);
    //                 }
    //             }
    //             return col.dataIndex?.toString() || '';
    //         });

    //         // Get rows from dataSource
    //         const rows: any[][] = transformedDataSource.map((record) => {
    //             return transformedColumns.map((col) => {
    //                 const dataIndex = col.dataIndex as string;
    //                 let value = record[dataIndex];

    //                 // If there's a custom render function, we need to get the actual value
    //                 // For now, we'll use the raw value from the record
    //                 if (value === null || value === undefined) {
    //                     return '';
    //                 }

    //                 // Handle objects and arrays
    //                 if (typeof value === 'object') {
    //                     return JSON.stringify(value);
    //                 }

    //                 return String(value);
    //             });
    //         });

    //         // Create workbook and worksheet
    //         const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    //         const workbook = XLSX.utils.book_new();
    //         XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    //         // Generate filename
    //         const filename = displayTitle
    //             ? `${displayTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_table_${Date.now()}.xlsx`
    //             : `table_${Date.now()}.xlsx`;

    //         // Save file
    //         XLSX.writeFile(workbook, filename);
    //     } catch (error) {
    //         console.error('Error exporting table to Excel:', error);
    //         console.error('Error details:', error instanceof Error ? error.stack : error);
    //     }
    // };

    // // Use provided onDownload or default Excel export
    // const handleDownload = onDownload || handleDefaultExcelExport;

    // // Show download button if onDownload is provided or if we have data to export
    // const showDownloadButton =
    //     onDownload !== undefined || (transformedDataSource.length > 0 && transformedColumns.length > 0);

    return (
        <div
            ref={tableRef}
            className={`data-table-container ${className}`}
            style={{ maxWidth }}
            data-test-id={dataTestId}
        >   
            <div className="flex justify-between items-center">
                {displayTitle && (
                    <div className="data-table-header m-4">
                        <h3 className="data-table-title font-semibold text-[16px]">{displayTitle}</h3>
                    </div>
                )}
            </div>
            <Table
                columns={finalColumns}
                dataSource={transformedDataSource}
                rowKey={rowKey}
                bordered={bordered}
                pagination={pagination}
                scroll={mergedScroll}
                loading={loading}
                className="data-table"
            />
        </div>
    );
};

export default DataTable;
